import { useEffect, useState } from 'react';
import * as Headless from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import { useGoQuery } from '../hooks/useGoQuery';
import { GoResultsList } from './GoResultsList';

export function GoSearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [value, setValue] = useState('');
  const [submittedValue, setSubmittedValue] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { submit, pick, reset, results, isPending, isError, noResult } = useGoQuery({
    onNavigate: () => {
      setValue('');
      onClose();
    },
  });

  useEffect(() => {
    if (!open) {
      setValue('');
      setSubmittedValue(null);
      // Cancel any in-flight navigation — once the dialog is dismissed,
      // a late success must not navigate the user away.
      reset({ cancelPending: true });
    }
    // `reset` is recreated each render but only touches state setters / refs
    // by identity — safe to omit from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  function handleChange(next: string) {
    setValue(next);
    setSubmittedValue(null);
    reset();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' && results.length > 0) {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
      return;
    }
    if (e.key === 'ArrowUp' && results.length > 0) {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      const openInNewTab = e.metaKey || e.ctrlKey;
      if (value === submittedValue && results.length > 0) {
        pick(results[selectedIndex], openInNewTab);
        return;
      }
      setSubmittedValue(value);
      submit(value, openInNewTab);
    }
  }

  return (
    <Headless.Dialog open={open} onClose={onClose} className="relative z-50">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 transition data-closed:opacity-0 data-enter:duration-150 data-enter:ease-out data-leave:duration-100 data-leave:ease-in"
      />

      <div className="fixed inset-0 flex items-start justify-center p-4 pt-[20vh]">
        <Headless.DialogPanel
          transition
          className="w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-zinc-950/5 transition duration-150 data-closed:scale-95 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in dark:bg-zinc-900 dark:ring-white/10"
        >
          <Headless.DialogTitle className="sr-only">Go to document, page, project, or organisation</Headless.DialogTitle>

          <div className="relative flex items-center gap-3 px-4">
            <MagnifyingGlassIcon className="size-5 shrink-0 text-zinc-400 dark:text-zinc-500" />
            <input
              autoFocus
              placeholder="Paste an ID, ID prefix, or document name…"
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="h-12 w-full border-0 bg-transparent text-sm text-zinc-900 outline-hidden placeholder:text-zinc-400 dark:text-white dark:placeholder:text-zinc-500"
            />
            {isPending && <FaSpinner className="shrink-0 animate-spin text-zinc-400" />}
            {isError && !isPending && <FaExclamationTriangle className="shrink-0 text-red-500" />}
          </div>

          {results.length > 0 && (
            <div className="border-t border-zinc-950/5 dark:border-white/10">
              <GoResultsList results={results} selectedIndex={selectedIndex} onHover={setSelectedIndex} onPick={pick} />
            </div>
          )}

          {(noResult || isError) && !isPending && (
            <div className="border-t border-zinc-950/5 px-4 py-3 text-sm text-zinc-500 dark:border-white/10 dark:text-zinc-400">
              {noResult ? (
                <>
                  No match for <span className="font-mono text-zinc-900 dark:text-white">{submittedValue}</span>
                </>
              ) : (
                <>Search failed — press Enter to retry</>
              )}
            </div>
          )}

          <div className="flex items-center gap-4 border-t border-zinc-950/5 px-4 py-2 text-xs text-zinc-400 dark:border-white/10 dark:text-zinc-500">
            <span>↑↓ navigate</span>
            <span>↵ open</span>
            <span>⌘↵ new tab</span>
            <span>esc close</span>
          </div>
        </Headless.DialogPanel>
      </div>
    </Headless.Dialog>
  );
}
